"use client";

import type * as React from "react";

// TODO: migrate to react aria alternative
import * as DialogPrimitive from "@radix-ui/react-dialog";
import Close24 from "@ui/icons/24x24/close-24.svg";
import { cn } from "@ui/lib/utils";
import clsx from "clsx";
import { useTranslations } from "next-intl";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogCloseButton = ({
  className,
  variant,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> & { variant?: "default" | "elevated" }) => {
  const t = useTranslations("ClientKeys.Globals");
  return (
    <DialogPrimitive.Close
      asChild
      className={cn("effect-focus absolute right-20 top-20 z-9999 rounded-full", className)}
      {...props}
    >
      <button
        className={clsx(
          "effect-opacity effect-focus-inverse flex h-40 w-40 items-center justify-center rounded-full bg-default text-default hover:opacity-85",
          variant === "elevated" && "shadow-combined",
        )}
        type='button'
      >
        <Close24 />
        <span className='sr-only'>{t("close")}</span>
      </button>
    </DialogPrimitive.Close>
  );
};

const DialogOverlay = ({
  className,
  ref,
  withCloseButton = true,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.Ref<HTMLDivElement>;
  withCloseButton?: boolean;
}) => {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-9999 bg-overlay-darkest-plus backdrop-blur-[18px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      ref={ref}
      {...props}
    >
      {withCloseButton && <DialogCloseButton />}
    </DialogPrimitive.Overlay>
  );
};
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  hasOverlay = true,
  overlayClassName,
  className,
  children,
  ref,
  closeButtonVariant = "outside",
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  closeButtonVariant?: "inside" | "outside";
  hasOverlay?: boolean;
  overlayClassName?: string;
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <DialogPortal>
    {hasOverlay && <DialogOverlay className={overlayClassName} withCloseButton={closeButtonVariant === "outside"} />}
    <DialogPrimitive.Content
      className={cn(
        "fixed left-[50%] top-[50%] z-9999 rounded-lg bg-default",
        "w-full translate-x-[-50%] translate-y-[-50%]",
        "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      {closeButtonVariant === "inside" && <DialogCloseButton variant='elevated' />}
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col justify-center space-y-4", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & { ref?: React.Ref<HTMLDivElement> }) => (
  <DialogPrimitive.Title className={cn("leading-none f-heading-05", className)} ref={ref} {...props} />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & { ref?: React.Ref<HTMLDivElement> }) => (
  <DialogPrimitive.Description className={cn("text-body", className)} ref={ref} {...props} />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
